/**
 * Google认证服务 - OAuth2授权码流程
 */
class GoogleAuthService {
  async getGoogleAuthConfig() {
    try {
      const axios = (await import('./api')).default;
      const response = await axios.get('/auth/google/code');
      return response.data;
    } catch (error) {
      console.error('获取Google登录配置失败:', error);
      const message = error.response?.data?.userMessage
        || error.response?.data?.message
        || error.message;
      throw new Error('获取配置失败: ' + message);
    }
  }

  /**
   * 使用Google OAuth2跳转方式进行登录
   * @param {string} referralCode 推荐码
   * @param {string} returnUrl 登录成功后要返回的页面URL
   */
  async loginWithGoogleRedirect(referralCode = null, returnUrl = null) {
    try {
      const config = await this.getGoogleAuthConfig();
      
      const baseUrl = config.baseUrl
      const redirectUri = config.redirectUri
      const clientId = config.clientId;
      
      if (!clientId) {
        throw new Error('未获取到有效的客户端ID');
      }
      
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid email profile',
        access_type: 'offline',
        prompt: 'consent'
      });

      // 如果有推荐码，保存到sessionStorage
      if (referralCode) {
        sessionStorage.setItem('google_auth_referral_code', referralCode);
      }

      // 保存登录前的页面URL，用于登录成功后跳转
      if (returnUrl) {
        sessionStorage.setItem('google_auth_return_url', returnUrl);
      } else {
        // 如果没有指定返回URL，使用当前页面
        sessionStorage.setItem('google_auth_return_url', window.location.pathname + window.location.search);
      }

      // 跳转到Google授权页面
      const authUrl = `${baseUrl}?${params.toString()}`;
      window.location.href = authUrl;
      
    } catch (error) {
      console.error('Google跳转登录失败:', error);
      throw new Error('Google跳转登录失败: ' + error.message);
    }
  }

  /**
   * 生成CSRF令牌
   */
  generateCSRFToken() {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * 保存认证信息到本地存储
   */
  saveAuthInfo(token, user, csrfToken = null) {
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      if (csrfToken) {
        localStorage.setItem('csrf_token', csrfToken);
      }
      
      // 设置axios默认header
      if (window.axios) {
        window.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      console.log('认证信息保存成功');
    } catch (error) {
      console.error('保存认证信息失败:', error);
      throw new Error('保存认证信息失败');
    }
  }

  /**
   * 清除认证信息
   */
  clearAuthInfo() {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('csrf_token');
      sessionStorage.removeItem('google_auth_referral_code');
      
      // 清除axios默认header
      if (window.axios) {
        delete window.axios.defaults.headers.common['Authorization'];
      }
      
      console.log('认证信息清除成功');
    } catch (error) {
      console.error('清除认证信息失败:', error);
    }
  }

  /**
   * 检查是否已认证
   */
  isAuthenticated() {
    try {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      return !!(token && user);
    } catch (error) {
      console.error('检查认证状态失败:', error);
      return false;
    }
  }

  /**
   * 检查是否已登录（别名方法）
   */
  isLoggedIn() {
    return this.isAuthenticated();
  }

  /**
   * 获取当前用户信息
   */
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('获取用户信息失败:', error);
      return null;
    }
  }

  /**
   * 获取当前token
   */
  getCurrentToken() {
    try {
      return localStorage.getItem('token');
    } catch (error) {
      console.error('获取token失败:', error);
      return null;
    }
  }

  /**
   * 登出
   */
  async logout() {
    try {
      // 清除本地认证信息
      this.clearAuthInfo();
      
      console.log('登出成功');
      return { success: true };
    } catch (error) {
      console.error('登出失败:', error);
      return { success: false, error: error.message };
    }
  }
}

// 创建单例实例
const googleAuthService = new GoogleAuthService();

export default googleAuthService;
